import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import AdminShell from "@/components/admin/AdminShell";
import SortablePhotoGrid from "@/components/collage/SortablePhotoGrid";
import LayoutWireframe from "@/components/collage/LayoutWireframe";
import CollagePage from "@/components/CollagePage";
import { TEMPLATES, getSlotCount } from "@/lib/layouts";
import LayoutRadioGrid from "@/components/collage/LayoutRadioGrid";
import { downloadAllAsPdf, downloadPageAsPdf, openPdfPreview } from "@/lib/pdf";
import {
  ArrowLeft,
  ArrowRight,
  UploadCloud,
  Download,
  Eye,
  FileDown,
  Plus,
  Trash2,
  Maximize2,
  Minimize2,
} from "lucide-react";

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

export default function CollageWorkflow() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);

  // -- shared config state
  const [templateId, setTemplateId] = useState("auto");
  const [photosPerPage, setPhotosPerPage] = useState(4);
  const [pageCount, setPageCount] = useState(1);
  const [images, setImages] = useState([]); // sorted
  const [pages, setPages] = useState([]);
  const [slotAdjustments, setSlotAdjustments] = useState({}); // pageId -> {slotIdx: {zoom, rotate}}
  const [exporting, setExporting] = useState(false);

  const pageRefs = useRef({});
  const registerRef = (idx, node) => (pageRefs.current[idx] = node);

  const imagesMap = useMemo(() => {
    const m = {};
    images.forEach((i) => (m[i.id] = i));
    return m;
  }, [images]);

  // Autosave to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(
      "sb_collage_config",
      JSON.stringify({ templateId, photosPerPage, pageCount }),
    );
  }, [templateId, photosPerPage, pageCount]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("sb_collage_config");
      if (raw) {
        const cfg = JSON.parse(raw);
        if (cfg.templateId) setTemplateId(cfg.templateId);
        if (cfg.photosPerPage) setPhotosPerPage(cfg.photosPerPage);
        if (cfg.pageCount) setPageCount(cfg.pageCount);
      }
    } catch (_e) {
      // ignore
    }
  }, []);

  const suggestedPages = useMemo(() => {
    const t = TEMPLATES[templateId];
    const slotsPerPage = t && t.slots !== null ? t.slots : photosPerPage;
    // Treat photosPerPage as the total capacity the user wants when it
    // exceeds a fixed template's slot count. Otherwise fall back to the
    // image count.
    const totalPhotos = Math.max(images.length, photosPerPage);
    return Math.max(1, Math.ceil(totalPhotos / slotsPerPage));
  }, [templateId, photosPerPage, images.length]);

  // When the template changes, pre-populate photos/page from the template's
  // slot count (fixed templates only). Dynamic templates leave the value alone.
  useEffect(() => {
    const t = TEMPLATES[templateId];
    if (t && t.slots !== null) {
      setPhotosPerPage(t.slots);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // Keep the "Pages" input synced with the suggested value automatically.
  // If the user later types a bigger number manually the input still wins,
  // but any change to the template / photos-per-page / image count will
  // re-sync it to the current suggestion.
  useEffect(() => {
    setPageCount(suggestedPages);
  }, [suggestedPages]);

  const totalSlots = useMemo(
    () => getSlotCount(templateId, photosPerPage),
    [templateId, photosPerPage],
  );

  // ---------- Step 1 handlers ----------
  const addFiles = (files) => {
    const arr = Array.from(files || []).filter((f) =>
      f.type.startsWith("image/"),
    );
    if (!arr.length) {
      toast.error("Please choose JPG, PNG or WEBP images.");
      return;
    }
    const newImgs = arr.map((f) => ({
      id: uid(),
      url: URL.createObjectURL(f),
      file: f,
    }));
    setImages((s) => [...s, ...newImgs]);
    toast.success(`${newImgs.length} photo${newImgs.length === 1 ? "" : "s"} added`);
  };

  const removeImage = (id) => {
    setImages((s) => {
      const t = s.find((x) => x.id === id);
      if (t) URL.revokeObjectURL(t.url);
      return s.filter((x) => x.id !== id);
    });
  };

  const goToStep2 = () => {
    if (images.length === 0) {
      toast.error("Upload at least one photo to continue.");
      return;
    }
    const slotsPerPage = getSlotCount(templateId, photosPerPage);
    const neededForImages = Math.max(1, Math.ceil(images.length / slotsPerPage));
    // For fixed templates, respect the "photos/page as total capacity" idea.
    const t = TEMPLATES[templateId];
    const neededForCapacity =
      t && t.slots !== null
        ? Math.max(1, Math.ceil(photosPerPage / t.slots))
        : 1;
    const actualPageCount = Math.max(
      pageCount,
      neededForImages,
      neededForCapacity,
    );
    const newPages = [];
    for (let i = 0; i < actualPageCount; i++) {
      const start = i * slotsPerPage;
      const assigned = images
        .slice(start, start + slotsPerPage)
        .map((im) => im.id);
      while (assigned.length < slotsPerPage) assigned.push(undefined);
      newPages.push({ id: uid(), templateId, imageIds: assigned });
    }
    setPages(newPages);
    setStep(2);
  };

  // ---------- Step 2 handlers ----------
  const onReplaceImage = (pageIndex, slotIndex, file) => {
    const url = URL.createObjectURL(file);
    const newImg = { id: uid(), url, file };
    setImages((s) => [...s, newImg]);
    setPages((prev) => {
      const next = [...prev];
      const p = next[pageIndex];
      if (!p) return prev;
      const ids = [...p.imageIds];
      ids[slotIndex] = newImg.id;
      next[pageIndex] = { ...p, imageIds: ids };
      return next;
    });
  };

  const onRemoveSlot = (pageIndex, slotIndex) => {
    setPages((prev) => {
      const next = [...prev];
      const p = next[pageIndex];
      if (!p) return prev;
      const ids = [...p.imageIds];
      ids[slotIndex] = undefined;
      next[pageIndex] = { ...p, imageIds: ids };
      return next;
    });
  };

  const onAdjust = (pageIndex, slotIndex, patch) => {
    const page = pages[pageIndex];
    if (!page) return;
    setSlotAdjustments((prev) => {
      const pageAdj = { ...(prev[page.id] || {}) };
      pageAdj[slotIndex] = { ...(pageAdj[slotIndex] || {}), ...patch };
      return { ...prev, [page.id]: pageAdj };
    });
  };

  const addPage = () => {
    const slotsPerPage = getSlotCount(templateId, photosPerPage);
    const empties = Array(slotsPerPage).fill(undefined);
    setPages((p) => [...p, { id: uid(), templateId, imageIds: empties }]);
  };

  const setFitAll = (fit) => {
    setSlotAdjustments((prev) => {
      const next = { ...prev };
      pages.forEach((p) => {
        const slots = getSlotCount(p.templateId, photosPerPage);
        const pageAdj = { ...(next[p.id] || {}) };
        for (let i = 0; i < slots; i++) {
          pageAdj[i] = { ...(pageAdj[i] || {}), fit };
        }
        next[p.id] = pageAdj;
      });
      return next;
    });
    toast.success(
      fit === "contain" ? "All slots set to Scale to Fit" : "All slots set to Scale to Fill",
    );
  };

  const removePage = (pageIndex) => {
    if (pages.length <= 1) {
      toast.error("You need at least one page.");
      return;
    }
    setPages((p) => p.filter((_, i) => i !== pageIndex));
  };

  const downloadSinglePdf = async (i) => {
    const node = pageRefs.current[i];
    if (!node) return;
    try {
      setExporting(true);
      await downloadPageAsPdf(node, `sky-basket-collage-page-${i + 1}.pdf`);
      toast.success(`Page ${i + 1} downloaded`);
    } catch (e) {
      console.error(e);
      toast.error("Could not export PDF");
    } finally {
      setExporting(false);
    }
  };

  const downloadAll = async () => {
    const nodes = pages.map((_, i) => pageRefs.current[i]).filter(Boolean);
    if (!nodes.length) return;
    try {
      setExporting(true);
      await downloadAllAsPdf(nodes, `sky-basket-collage-${pages.length}-pages.pdf`);
      toast.success("Combined PDF downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Could not export combined PDF");
    } finally {
      setExporting(false);
    }
  };

  const previewSinglePdf = async (i) => {
    const node = pageRefs.current[i];
    if (!node) return;
    try {
      setExporting(true);
      await openPdfPreview.page(node);
    } catch (e) {
      console.error(e);
      toast.error("Could not open PDF preview");
    } finally {
      setExporting(false);
    }
  };

  const previewAll = async () => {
    const nodes = pages.map((_, i) => pageRefs.current[i]).filter(Boolean);
    if (!nodes.length) return;
    try {
      setExporting(true);
      await openPdfPreview.all(nodes);
    } catch (e) {
      console.error(e);
      toast.error("Could not open combined PDF preview");
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminShell
      breadcrumbs={[{ label: "Photo Collage Creator" }]}
      step={step}
    >
      {step === 1 ? (
        <Step1
          templateId={templateId}
          setTemplateId={setTemplateId}
          photosPerPage={photosPerPage}
          setPhotosPerPage={setPhotosPerPage}
          pageCount={pageCount}
          setPageCount={setPageCount}
          totalSlots={totalSlots}
          suggestedPages={suggestedPages}
          images={images}
          onAdd={addFiles}
          onReorder={setImages}
          onRemove={removeImage}
          onNext={goToStep2}
          onBack={() => nav("/admin")}
        />
      ) : (
        <Step2
          pages={pages}
          photosPerPage={photosPerPage}
          imagesMap={imagesMap}
          slotAdjustments={slotAdjustments}
          registerRef={registerRef}
          onReplaceImage={onReplaceImage}
          onRemoveSlot={onRemoveSlot}
          onAdjust={onAdjust}
          onAddPage={addPage}
          onRemovePage={removePage}
          onDownloadSingle={downloadSinglePdf}
          onDownloadAll={downloadAll}
          onPreviewSingle={previewSinglePdf}
          onPreviewAll={previewAll}
          onFitAll={() => setFitAll("contain")}
          onFillAll={() => setFitAll("cover")}
          exporting={exporting}
          onBack={() => setStep(1)}
        />
      )}
    </AdminShell>
  );
}

// ------------------------------------------------------------------
function Step1({
  templateId,
  setTemplateId,
  photosPerPage,
  setPhotosPerPage,
  pageCount,
  setPageCount,
  totalSlots,
  suggestedPages,
  images,
  onAdd,
  onReorder,
  onRemove,
  onNext,
  onBack,
}) {
  const fileRef = useRef(null);
  const isDynamic = TEMPLATES[templateId]?.slots === null;

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-8 py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#cb05a5]">
            Step 1 · Configure
          </div>
          <h1 className="mt-2 font-display font-black text-3xl md:text-4xl text-slate-900 tracking-tight">
            Photo Collage Creator
          </h1>
          <p className="mt-2 text-slate-600 max-w-2xl">
            Pick a layout, upload photos, drag to arrange the order, then move
            on to fine-tune each page.
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          data-testid="step1-back-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-slate-600 hover:text-slate-900 hover:bg-white transition-colors"
        >
          <ArrowLeft size={16} /> Back to dashboard
        </button>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* LEFT: controls */}
        <div className="space-y-6">
          <Card title="Layout Type">
            <LayoutRadioGrid
              value={templateId}
              onChange={setTemplateId}
              photosPerPage={photosPerPage}
            />
          </Card>

          <div className="grid grid-cols-3 gap-4">
            <Card title="Number of Slots">
              <input
                type="number"
                min={1}
                max={30}
                value={totalSlots}
                readOnly={!isDynamic}
                onChange={(e) => {
                  if (!isDynamic) return;
                  const v = Math.max(1, Math.min(30, Number(e.target.value) || 1));
                  setPhotosPerPage(v);
                }}
                data-testid="config-slots"
                className={`w-full px-3 py-2.5 rounded-xl border border-slate-200 font-medium ${
                  isDynamic ? "bg-white focus:border-[#299fe0]" : "bg-slate-100 text-slate-500"
                }`}
              />
              <div className="mt-1 text-[11px] text-slate-500">
                {isDynamic ? "Auto layouts: equals photos/page" : "Fixed by template"}
              </div>
            </Card>

            <Card title="Photos / Page">
              <input
                type="number"
                min={1}
                max={30}
                value={photosPerPage}
                onChange={(e) => {
                  const v = Math.max(1, Math.min(30, Number(e.target.value) || 1));
                  setPhotosPerPage(v);
                }}
                data-testid="config-photos-per-page"
                className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-[#299fe0] focus:ring-4 focus:ring-[#299fe0]/15 outline-none font-medium"
              />
            </Card>

            <Card title="Pages">
              <input
                type="number"
                min={1}
                max={50}
                value={pageCount}
                onChange={(e) => {
                  const v = Math.max(1, Math.min(50, Number(e.target.value) || 1));
                  setPageCount(v);
                }}
                data-testid="config-pages"
                className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-[#299fe0] focus:ring-4 focus:ring-[#299fe0]/15 outline-none font-medium"
              />
              <div className="mt-1 text-[11px] text-slate-500">
                Auto suggests: {suggestedPages}
              </div>
            </Card>
          </div>

          <Card title="Upload Photos">
            <div
              data-testid="collage-dropzone"
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add(
                  "border-[#299fe0]",
                  "bg-[#299fe0]/5",
                );
              }}
              onDragLeave={(e) =>
                e.currentTarget.classList.remove(
                  "border-[#299fe0]",
                  "bg-[#299fe0]/5",
                )
              }
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove(
                  "border-[#299fe0]",
                  "bg-[#299fe0]/5",
                );
                onAdd(e.dataTransfer.files);
              }}
              onClick={() => fileRef.current?.click()}
              className="cursor-pointer border-2 border-dashed border-slate-200 hover:border-[#299fe0] rounded-2xl p-8 flex flex-col items-center justify-center text-center transition-colors"
            >
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center text-white"
                style={{ background: "linear-gradient(135deg,#299fe0,#cb05a5)" }}
              >
                <UploadCloud size={22} />
              </div>
              <div className="mt-3 font-semibold text-slate-800">
                Drop images or click to browse
              </div>
              <div className="mt-1 text-xs text-slate-500">
                JPG · JPEG · PNG · WEBP · multiple allowed
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                multiple
                className="hidden"
                data-testid="collage-file-input"
                onChange={(e) => {
                  onAdd(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>
          </Card>

          <Card
            title={`Arrange Photos (${images.length})`}
            action={
              images.length ? (
                <span className="text-xs text-slate-500">
                  Drag the bottom handle to reorder
                </span>
              ) : null
            }
          >
            {images.length === 0 ? (
              <div className="text-center text-slate-400 py-8 text-sm">
                No photos yet — upload to start arranging.
              </div>
            ) : (
              <SortablePhotoGrid
                images={images}
                onReorder={onReorder}
                onRemove={onRemove}
              />
            )}
          </Card>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onNext}
              disabled={images.length === 0}
              data-testid="generate-collage-btn"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-white font-semibold shadow-lg disabled:opacity-60 disabled:cursor-not-allowed hover:-translate-y-0.5 transition-transform"
              style={{
                background: "linear-gradient(135deg,#299fe0,#cb05a5 140%)",
              }}
            >
              Generate Collage <ArrowRight size={16} />
            </button>
          </div>
        </div>

        {/* RIGHT: live wireframe */}
        <div className="lg:sticky lg:top-32 h-fit">
          <div className="rounded-3xl bg-white sb-hairline sb-shadow p-6 md:p-8">
            <LayoutWireframe
              templateId={templateId}
              photosPerPage={photosPerPage}
              pageCount={Math.max(pageCount, suggestedPages)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ title, action, children }) {
  return (
    <div className="rounded-2xl bg-white sb-hairline sb-shadow p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-[0.25em] font-semibold text-slate-500">
          {title}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// ------------------------------------------------------------------
function Step2({
  pages,
  photosPerPage,
  imagesMap,
  slotAdjustments,
  registerRef,
  onReplaceImage,
  onRemoveSlot,
  onAdjust,
  onAddPage,
  onRemovePage,
  onDownloadSingle,
  onDownloadAll,
  onPreviewSingle,
  onPreviewAll,
  onFitAll,
  onFillAll,
  exporting,
  onBack,
}) {
  return (
    <div className="mx-auto max-w-6xl px-6 md:px-8 py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#cb05a5]">
            Step 2 · Edit &amp; Export
          </div>
          <h1 className="mt-2 font-display font-black text-3xl md:text-4xl text-slate-900 tracking-tight">
            Review each page &amp; download PDFs
          </h1>
          <p className="mt-2 text-slate-600 max-w-2xl">
            Hover a photo to change how it fits, rotate, replace or remove it.
            Scroll on a photo to zoom. Removing a photo leaves the slot empty.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onBack}
            data-testid="step2-back-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-slate-600 hover:text-slate-900 hover:bg-white transition-colors"
          >
            <ArrowLeft size={16} /> Configure again
          </button>

          <div
            className="inline-flex items-center bg-white border border-slate-200 rounded-full p-1"
            data-testid="fit-mode-toggle"
          >
            <button
              type="button"
              onClick={onFillAll}
              data-testid="fit-all-fill-btn"
              title="Scale all photos to fill (crop to slot)"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              <Minimize2 size={13} /> Fill all
            </button>
            <button
              type="button"
              onClick={onFitAll}
              data-testid="fit-all-fit-btn"
              title="Scale all photos to fit (letterbox in slot)"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              <Maximize2 size={13} /> Fit all
            </button>
          </div>

          <button
            type="button"
            onClick={onAddPage}
            data-testid="add-page-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 hover:border-slate-900 text-slate-800 bg-white font-semibold transition-colors"
          >
            <Plus size={14} /> Add page
          </button>
          <button
            type="button"
            onClick={onPreviewAll}
            disabled={exporting || pages.length === 0}
            data-testid="preview-all-pdf-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-300 bg-white text-slate-800 hover:border-[#299fe0] hover:text-[#299fe0] font-semibold transition-colors disabled:opacity-60"
          >
            <Eye size={14} /> Preview PDF
          </button>
          <button
            type="button"
            onClick={onDownloadAll}
            disabled={exporting || pages.length === 0}
            data-testid="download-all-pdf-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-semibold shadow disabled:opacity-60"
            style={{ background: "linear-gradient(135deg,#299fe0,#cb05a5 140%)" }}
          >
            <FileDown size={16} />
            {exporting ? "Exporting…" : "Combine & Download All"}
          </button>
        </div>
      </div>

      <div className="mt-10 space-y-14">
        {pages.map((p, i) => (
          <div key={p.id} className="space-y-4">
            <CollagePage
              page={p}
              pageIndex={i}
              photosPerPage={photosPerPage}
              imagesMap={imagesMap}
              registerRef={registerRef}
              onReplaceImage={onReplaceImage}
              onRemoveImage={onRemoveSlot}
              onAdjust={onAdjust}
              slotAdjustments={slotAdjustments}
            />
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => onPreviewSingle(i)}
                disabled={exporting}
                data-testid={`preview-page-pdf-${i}`}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-300 bg-white text-slate-800 hover:border-[#299fe0] hover:text-[#299fe0] font-semibold transition-colors disabled:opacity-60"
              >
                <Eye size={14} /> Preview PDF
              </button>
              <button
                type="button"
                onClick={() => onDownloadSingle(i)}
                disabled={exporting}
                data-testid={`download-page-pdf-${i}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-slate-900 border border-slate-900 hover:bg-slate-900 hover:text-white font-semibold transition-colors"
              >
                <Download size={14} /> Download this page (PDF)
              </button>
              {pages.length > 1 && (
                <button
                  type="button"
                  onClick={() => onRemovePage(i)}
                  data-testid={`remove-page-${i}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-200 transition-colors"
                >
                  <Trash2 size={14} /> Remove page
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
